import { useEffect, useRef, useState } from "react"
import { confirmUserAttribute, sendUserAttributeVerificationCode, updateUserAttribute } from "aws-amplify/auth"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Maps Amplify/Cognito exception names to friendly copy; InvalidParameter
// falls through to Cognito's own message (it names the offending field).
function emailErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    switch (err.name) {
      case "AliasExistsException": return "That email address is already used by another account."
      case "CodeMismatchException": return "That code didn't match. Please try again."
      case "ExpiredCodeException": return "That code has expired — request a new one."
      case "LimitExceededException":
      case "TooManyRequestsException": return "Too many attempts — please wait a while and try again."
      case "NotAuthorizedException": return "Your session has expired. Please sign in again."
    }
    return err.message
  }
  return "unknown error"
}

// In-app email change via browser→Cognito UpdateUserAttributes + code
// confirmation. Both pools keep the ORIGINAL address active until the new
// one is verified ("keep original attribute value active when an update is
// pending"), so a typo can't lock the user out of email-based sign-in.
// onClose(changed) — the Account page refetches /me when changed is true.
export default function ChangeEmailDialog({ currentEmail, onClose }: { currentEmail: string, onClose: (changed: boolean) => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"form" | "busy" | "code" | "confirming" | "success">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")
  const [destination, setDestination] = useState("")
  const [resent, setResent] = useState(false)
  const changed = useRef(false)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "busy" || step === "confirming"
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const sameAsCurrent = email.trim().toLowerCase() === currentEmail.toLowerCase()

  const handleSubmitEmail = async () => {
    setStep("busy")
    setErrorMessage("")
    try {
      // stored lowercased like every other address write (sign-up, the
      // sign-up correction endpoint, guest orders)
      const address = email.trim().toLowerCase()
      const { nextStep } = await updateUserAttribute({ userAttribute: { attributeKey: "email", value: address } })
      if (nextStep.updateAttributeStep === "CONFIRM_ATTRIBUTE_WITH_CODE") {
        setDestination(nextStep.codeDeliveryDetails?.destination ?? address)
        setStep("code")
      } else {
        changed.current = true
        setStep("success")
      }
    } catch (err) {
      setErrorMessage(emailErrorMessage(err))
      setStep("form")
    }
  }

  const handleConfirm = async () => {
    setStep("confirming")
    setErrorMessage("")
    try {
      await confirmUserAttribute({ userAttributeKey: "email", confirmationCode: code })
      changed.current = true
      setStep("success")
    } catch (err) {
      setErrorMessage(emailErrorMessage(err))
      setStep("code")
    }
  }

  const handleResend = async () => {
    setErrorMessage("")
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: "email" })
      setResent(true)
    } catch (err) {
      setErrorMessage(emailErrorMessage(err))
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={() => onClose(changed.current)}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Change Email</h2>

        {(step === "form" || step === "busy") && (
          <>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Current email</span>
                <input type="email" value={currentEmail} className="generate-codes-input" disabled readOnly />
              </label>
              <label className="generate-codes-field">
                <span className="generate-codes-label">New email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                />
                <span className="generate-codes-hint">We'll send a verification code to the new address. Your current address keeps working until you confirm it.</span>
              </label>
            </div>

            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" disabled={busy || !emailValid || sameAsCurrent} onClick={handleSubmitEmail}>
                {busy ? <><Spinner size={12} /> Sending…</> : "Send code"}
              </button>
            </div>
          </>
        )}

        {(step === "code" || step === "confirming") && (
          <>
            <p className="generate-codes-success">Enter the code we sent to {destination}.</p>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Verification code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                />
                <span className="generate-codes-hint">
                  {resent ? "A new code is on its way." : (
                    <button className="link" onClick={handleResend} disabled={busy} style={{ background: "none", border: "none", padding: 0, color: "var(--nfa-blue)", cursor: "pointer", font: "inherit" }}>
                      Resend code
                    </button>
                  )}
                </span>
              </label>
            </div>

            {errorMessage && <p className="generate-codes-error">{errorMessage}</p>}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" disabled={busy || code.length !== 6} onClick={handleConfirm}>
                {busy ? <><Spinner size={12} /> Confirming…</> : "Confirm"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">Email address updated.</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
